import { useState } from "react";
import type { FormEvent } from "react";
import PageShell from "@/components/feature/PageShell";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";

const FORM_ID = "age-about-feedback";
const FORM_ACTION = "https://readdy.ai/api/form/db9kat9rh5tg6sock71g";

const principles = [
  {
    title: "只写有信息量的内容",
    desc: "不追逐无意义的热点，每篇资讯都尽量交代清楚来源、时间与背景，让读者读完能获得确定的信息。",
    icon: "ri-file-text-line",
  },
  {
    title: "分析基于作品本身",
    desc: "角色解析以剧情与演出细节为依据，不臆测设定、不编造访谈，观点与事实分开表达。",
    icon: "ri-quill-pen-line",
  },
  {
    title: "尊重创作与版权",
    desc: "站内配图与作品信息均为演示内容，不提供任何影视资源下载或盗版链接。",
    icon: "ri-shield-check-line",
  },
];

const team = [
  { name: "林默", role: "内容主编", desc: "负责资讯选题与产业观察，关注制作委员会与发行市场。", initial: "林", color: "bg-primary-100 text-primary-700" },
  { name: "苏禾", role: "作品编辑", desc: "新番与剧场版的第一手观剧记录，偏爱日常与青春题材。", initial: "苏", color: "bg-secondary-100 text-secondary-900" },
  { name: "顾南", role: "专栏作者", desc: "角色分析与演出研究，习惯逐帧看分镜。", initial: "顾", color: "bg-accent-100 text-accent-700" },
];

export default function AboutPage() {
  useSeo(
    "关于我们 - age动漫",
    "了解age动漫的网站定位、内容准则与运营团队。age动漫专注动漫资讯、作品展示与角色分析，为动漫爱好者提供有信息量的中文内容。",
    "关于age动漫,age动漫团队,动漫资讯网站,内容准则",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    if (!payload.get("name") || !payload.get("email") || !payload.get("message")) {
      setStatus("error");
      setFormError("请填写称呼、邮箱与反馈内容后再提交。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMessage = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = String(serverMessage || "").toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setFormError(
          serverMessage && !isSpam
            ? String(serverMessage)
            : "提交未成功，请稍后重试，或稍后再来反馈。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎有点问题，请检查连接后重试。");
    }
  };

  return (
    <PageShell>
      <PageHeader
        breadcrumb={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
        kicker="ABOUT"
        title="关于 age动漫"
        description="我们是一个专注动漫内容整理的中文编辑团队，做资讯、做作品介绍，也做角色解析。不是资源站，不提供下载，只想把值得读的内容认真写好。"
        meta={
          <div className="flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-team-line text-primary-500"></i>
              3 人编辑团队
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-500"></i>
              自 2023 年更新
            </span>
          </div>
        }
      />

      <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12 flex flex-col gap-10 md:gap-14">
        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-6 lg:gap-10 items-start">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">网站定位</h2>
            <div className="mt-3 space-y-3.5 text-[15px] leading-7 md:leading-8 text-foreground-700">
              <p>
                age动漫成立于 2023 年，最初只是几个朋友之间的追番笔记。随着内容越来越多，我们把它整理成了一个公开的内容门户：一边记录行业里正在发生的事，一边认真聊那些值得被反复讨论的作品与角色。
              </p>
              <p>
                我们不提供在线观看与资源下载，也不会用夸张的标题换取点击。这里的内容按资讯、作品、角色三个方向组织，每一篇都尽量交代清楚时间、来源与作者，让读者可以自己判断信息的价值。
              </p>
              <p>
                目前站内的作品与人物信息均为演示内容，用于展示内容浏览与阅读体验；正式运营后，我们会逐步接入真实的作品资料与授权配图。
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">内容覆盖</h3>
            <ul className="flex flex-col gap-3">
              {[
                { label: "行业动态与产业观察", icon: "ri-line-chart-line", value: "每日" },
                { label: "新番与剧场版介绍", icon: "ri-film-line", value: "每周" },
                { label: "角色深度解析", icon: "ri-user-star-line", value: "每周" },
                { label: "观番推荐合集", icon: "ri-list-check-2", value: "每月" },
              ].map((row) => (
                <li key={row.label} className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 text-foreground-700">
                    <i className={`${row.icon} text-primary-500`}></i>
                    {row.label}
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-background-50 text-foreground-500 border border-background-200 whitespace-nowrap">
                    {row.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="about-principles">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-[3px] rounded-full bg-primary-500"></span>
            <span className="text-[11px] font-medium tracking-[0.16em] uppercase text-primary-600">PRINCIPLES</span>
          </div>
          <h2 id="about-principles" className="text-lg md:text-xl font-bold text-foreground-950">
            内容准则
          </h2>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 mb-3">
                  <i className={`${item.icon} text-base`}></i>
                </span>
                <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="about-team" className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
          <h2 id="about-team" className="text-lg md:text-xl font-bold text-foreground-950">
            运营团队
          </h2>
          <p className="mt-2 text-sm text-foreground-500 leading-relaxed max-w-2xl">
            一个不大的编辑部，分工很简单：有人负责跑资讯，有人负责看片写介绍，有人专门研究角色与演出。所有内容都会经过至少两次校对再发布。
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg bg-background-50 border border-background-200 p-4 flex gap-3">
                <span
                  className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-base shrink-0 ${member.color}`}
                >
                  {member.initial}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground-900">{member.name}</h3>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">
                      {member.role}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{member.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="feedback" className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6 lg:gap-10 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-5 h-[3px] rounded-full bg-primary-500"></span>
              <span className="text-[11px] font-medium tracking-[0.16em] uppercase text-primary-600">CONTACT</span>
            </div>
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              内容纠错、选题建议、合作咨询，都欢迎通过右侧表单告诉我们。我们通常在两个工作日内查看反馈，如果涉及内容错误，会优先处理。
            </p>
            <ul className="mt-5 flex flex-col gap-3 text-sm text-foreground-700">
              <li className="flex items-center gap-2.5">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                contact@ageanime.example
              </li>
              <li className="flex items-center gap-2.5">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-map-pin-line"></i>
                </span>
                线上内容团队 · 不设线下接待
              </li>
              <li className="flex items-center gap-2.5">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-time-line"></i>
                </span>
                反馈处理时间：周一至周五
              </li>
            </ul>
            <div className="mt-5 rounded-lg border border-secondary-100 bg-secondary-50 p-4">
              <p className="text-xs text-foreground-600 leading-relaxed">
                提示：本站不收集任何与浏览无关的个人信息，反馈表单仅用于内容沟通。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h3 className="text-sm font-semibold text-foreground-900">意见反馈表单</h3>
            <p className="mt-1 text-xs text-foreground-500">带 * 的为必填项，反馈内容不超过 500 字。</p>

            {status === "success" ? (
              <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-5 text-center">
                <span className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-500 text-background-50 mb-3">
                  <i className="ri-check-line text-xl"></i>
                </span>
                <h4 className="text-sm font-semibold text-foreground-900">反馈已提交，感谢你的留言</h4>
                <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                  我们会在工作日尽快查看。如需继续反馈，可以再填写一次表单。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-primary-300 text-sm text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id={FORM_ID}
                data-readdy-form
                action={FORM_ACTION}
                method="post"
                onSubmit={handleSubmit}
                className="mt-4 flex flex-col gap-3.5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label htmlFor="feedback-name" className="block text-xs text-foreground-600 mb-1.5">
                      你的称呼 *
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className="block text-xs text-foreground-600 mb-1.5">
                      邮箱 *
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于回复你"
                      className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-topic" className="block text-xs text-foreground-600 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="feedback-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="feedback-message" className="block text-xs text-foreground-600 mb-1.5">
                    反馈内容 *
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请具体描述你遇到的问题或建议"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-y"
                  ></textarea>
                  <div className="mt-1 flex justify-end text-[11px] text-foreground-400">{messageLength} / 500</div>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="form-extra-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2 leading-relaxed">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:hover:bg-primary-500 transition-colors cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            )}
          </div>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950">版权声明与浏览说明</h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs md:text-sm text-foreground-600 leading-relaxed">
            <p>
              本站所有文字内容（资讯、作品介绍、角色分析）均为 age动漫编辑团队原创或整理，用于内容展示与浏览体验验证，未经许可请勿用于商业用途。站内出现的作品名称、角色名称与配图仅作演示，相关权利归原权利人所有。
            </p>
            <p>
              本站不提供任何动画资源的在线播放、下载或分享链接。作品详情页的视频预告模块仅为前端交互演示，不包含真实流媒体内容。如发现内容存在事实性错误，欢迎通过上方表单反馈，我们会尽快核实修正。
            </p>
          </div>
          <p className="mt-4 pt-4 border-t border-background-200 text-[11px] text-foreground-400">
            © 2026 age动漫 · 保留所有权利 · 建议使用最新版 Chrome、Edge 或 Safari 浏览，以获得完整的交互体验。
          </p>
        </section>
      </div>
    </PageShell>
  );
}